import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Star, ArrowClockwise, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Phase 3 — Admin widget showing the distribution of prospect quality
 * tiers across the DB + a one-click recompute button.
 */
const TIER_ORDER = ["Excellent", "Bon", "Moyen", "Faible", "Exclure"];
const TIER_COLOR = {
  Excellent: "bg-primary text-primary-foreground",
  Bon:       "bg-emerald-600 text-white",
  Moyen:     "bg-amber-500 text-white",
  Faible:    "bg-orange-500 text-white",
  Exclure:   "bg-secondary text-secondary-foreground",
};

export default function ProspectQualityWidget() {
  const [stats, setStats] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => {
    api.get("/admin/prospects/quality/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats({ tiers: {}, total_scored: 0, total_businesses: 0, coverage_pct: 0 }));
  };
  useEffect(() => { load(); }, []);

  const recompute = async () => {
    if (!window.confirm("Lancer le recalcul complet de tous les scores qualité ?\n\nÇa peut prendre plusieurs minutes sur 115k+ fiches (fait en arrière-plan).")) return;
    setRunning(true);
    try {
      await api.post("/admin/prospects/quality/recompute");
      toast.success("Recalcul lancé en arrière-plan. Reviens dans quelques minutes pour la distribution mise à jour.");
      setTimeout(load, 3000);
    } catch {
      toast.error("Erreur au lancement du recalcul.");
    } finally {
      setRunning(false);
    }
  };

  const totalScored = stats?.total_scored || 0;
  return (
    <div className="bg-card border border-border rounded-sm p-4 mb-6" data-testid="widget-prospect-quality">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="label-uppercase text-primary flex items-center gap-1.5">
            <Star size={12} weight="fill"/> Score qualité prospects (Phase 3)
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Répartition 0-100 · recalculé automatiquement après chaque feedback télépro.
          </div>
        </div>
        <button
          onClick={recompute}
          disabled={running}
          className="text-primary hover:text-[#0f3d29] text-[11px] font-semibold inline-flex items-center gap-0.5 shrink-0 disabled:opacity-50"
          data-testid="quality-recompute-btn"
          title="Recalcule tous les scores (fond, ~5 min sur 115k fiches)"
        >
          <ArrowClockwise size={10} weight="bold" className={running ? "animate-spin" : ""}/> Recalculer tout
        </button>
      </div>
      {!stats ? (
        <div className="text-xs text-muted-foreground py-4">Chargement…</div>
      ) : totalScored === 0 ? (
        <div className="bg-accent/20 border border-dashed border-border rounded-sm p-4 text-center">
          <ChartBar size={20} weight="duotone" className="text-muted-foreground mx-auto mb-1"/>
          <div className="text-sm">Aucun score calculé pour le moment.</div>
          <div className="text-[11px] text-muted-foreground mt-1">Clique <b>Recalculer tout</b> pour lancer le premier scoring.</div>
        </div>
      ) : (
        <>
          <div className="text-[11px] text-muted-foreground mb-2 flex items-center gap-2 flex-wrap">
            <span><b className="text-foreground tabular-nums">{totalScored.toLocaleString("fr-FR")}</b> / {(stats.total_businesses || 0).toLocaleString("fr-FR")} fiches scorées</span>
            <span className="text-border">·</span>
            <span>Couverture <b className="text-foreground tabular-nums">{stats.coverage_pct}%</b></span>
          </div>
          {/* Horizontal stacked bar */}
          <div className="flex w-full h-3 rounded-sm overflow-hidden bg-muted mb-2" data-testid="quality-distribution-bar">
            {TIER_ORDER.map((tier) => {
              const t = stats.tiers?.[tier];
              const count = t?.count || 0;
              const pct = totalScored ? (count / totalScored) * 100 : 0;
              if (pct === 0) return null;
              return (
                <div
                  key={tier}
                  className={`${TIER_COLOR[tier]} h-full transition-all`}
                  style={{ width: `${pct}%` }}
                  title={`${tier} — ${count} fiches (${pct.toFixed(1)}%)`}
                  data-testid={`quality-bar-${tier}`}
                />
              );
            })}
          </div>
          {/* Legend + counts */}
          <div className="grid grid-cols-5 gap-1.5 text-[11px]" data-testid="quality-tier-legend">
            {TIER_ORDER.map((tier) => {
              const t = stats.tiers?.[tier];
              const count = t?.count || 0;
              const avg = t?.avg_score || 0;
              return (
                <div key={tier} className="bg-accent/20 border border-border rounded-sm px-2 py-1.5 text-center" data-testid={`quality-tier-${tier}`}>
                  <div className={`inline-block w-2 h-2 rounded-full ${TIER_COLOR[tier]} mb-0.5`}/>
                  <div className="font-bold text-xs">{tier}</div>
                  <div className="tabular-nums font-mono text-foreground">{count.toLocaleString("fr-FR")}</div>
                  <div className="text-[10px] text-muted-foreground tabular-nums">moy. {avg}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
